<script setup lang="ts">
/**
 * Resize handles around a box — a report element, a dashboard tile, a shape on
 * a canvas. Place it inside the box (the box needs `position: relative`); the
 * handles sit on its edges and corners. Dragging a handle reports the pointer
 * movement since the drag started as `{ dx, dy, direction }` — the caller
 * turns that into a new size and position (so snapping to its own grid, min
 * sizes and aspect locks stay the caller's). A `grid` prop snaps the deltas.
 *
 * Keyboard: each handle is focusable; the arrow keys resize by `step` px
 * (`bigStep` with Shift), only along the axes that handle moves. Handles show
 * while `visible` (the box is selected) and whenever one has focus.
 */
import { computed, ref } from 'vue';

export type NxResizeDirection = 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw';

export interface NxResizeDelta {
  direction: NxResizeDirection;
  /** Movement since the resize started, in px (snapped to `grid` when set). */
  dx: number;
  dy: number;
}

const ALL: readonly NxResizeDirection[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

const LABELS: Record<NxResizeDirection, string> = {
  n: 'oben',
  ne: 'oben rechts',
  e: 'rechts',
  se: 'unten rechts',
  s: 'unten',
  sw: 'unten links',
  w: 'links',
  nw: 'oben links',
};

const props = withDefaults(
  defineProps<{
    directions?: readonly NxResizeDirection[];
    /** Show the handles (e.g. while the box is selected). Focused handles always show. */
    visible?: boolean;
    /** Snap step for the deltas in px; `0` = free. */
    grid?: number;
    /** Keyboard step in px, and with Shift. */
    step?: number;
    bigStep?: number;
    disabled?: boolean;
    /** Accessible name prefix: „Größe ändern“ → „Größe ändern: unten rechts“. */
    label?: string;
  }>(),
  {
    directions: () => ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'],
    visible: true,
    grid: 0,
    step: 8,
    bigStep: 32,
    disabled: false,
    label: 'Größe ändern',
  }
);

const emit = defineEmits<{
  'resize-start': [direction: NxResizeDirection];
  resize: [delta: NxResizeDelta];
  'resize-end': [delta: NxResizeDelta];
}>();

const shown = computed(() => ALL.filter((d) => props.directions.includes(d)));
const dragging = ref<NxResizeDirection | null>(null);

/** Which axes a handle moves: an edge one, a corner both. */
function axes(direction: NxResizeDirection): { x: boolean; y: boolean } {
  return { x: /[ew]/.test(direction), y: /[ns]/.test(direction) };
}

function snap(value: number): number {
  return props.grid > 0 ? Math.round(value / props.grid) * props.grid : value;
}

function delta(direction: NxResizeDirection, dx: number, dy: number): NxResizeDelta {
  const a = axes(direction);
  return { direction, dx: a.x ? snap(dx) : 0, dy: a.y ? snap(dy) : 0 };
}

let start = { x: 0, y: 0 };
let last: NxResizeDelta | null = null;

function onPointerDown(event: PointerEvent, direction: NxResizeDirection) {
  if (props.disabled || event.button !== 0) return;
  event.preventDefault();
  event.stopPropagation();
  const el = event.currentTarget as HTMLElement;
  el.setPointerCapture?.(event.pointerId);
  start = { x: event.clientX, y: event.clientY };
  last = delta(direction, 0, 0);
  dragging.value = direction;
  emit('resize-start', direction);
}

function onPointerMove(event: PointerEvent, direction: NxResizeDirection) {
  if (dragging.value !== direction) return;
  const next = delta(direction, event.clientX - start.x, event.clientY - start.y);
  if (last && next.dx === last.dx && next.dy === last.dy) return;
  last = next;
  emit('resize', next);
}

function onPointerUp(event: PointerEvent, direction: NxResizeDirection) {
  if (dragging.value !== direction) return;
  (event.currentTarget as HTMLElement).releasePointerCapture?.(event.pointerId);
  dragging.value = null;
  emit('resize-end', last ?? delta(direction, 0, 0));
  last = null;
}

function onKeydown(event: KeyboardEvent, direction: NxResizeDirection) {
  if (props.disabled) return;
  const size = event.shiftKey ? props.bigStep : props.step;
  const a = axes(direction);
  let dx = 0;
  let dy = 0;
  if (event.key === 'ArrowLeft' && a.x) dx = -size;
  else if (event.key === 'ArrowRight' && a.x) dx = size;
  else if (event.key === 'ArrowUp' && a.y) dy = -size;
  else if (event.key === 'ArrowDown' && a.y) dy = size;
  else return;
  event.preventDefault();
  const d = { direction, dx, dy };
  emit('resize-start', direction);
  emit('resize', d);
  emit('resize-end', d);
}
</script>

<template>
  <div
    class="nx-resize-handles"
    :class="{ 'is-visible': props.visible, 'is-dragging': dragging !== null }"
  >
    <span
      v-for="direction in shown"
      :key="direction"
      class="nx-resize-handles__handle"
      :class="[`dir-${direction}`, { 'is-active': dragging === direction }]"
      role="button"
      :tabindex="props.disabled ? -1 : 0"
      :aria-label="`${props.label}: ${LABELS[direction]}`"
      :aria-disabled="props.disabled || undefined"
      :data-direction="direction"
      @pointerdown="onPointerDown($event, direction)"
      @pointermove="onPointerMove($event, direction)"
      @pointerup="onPointerUp($event, direction)"
      @pointercancel="onPointerUp($event, direction)"
      @keydown="onKeydown($event, direction)"
    />
  </div>
</template>

<style scoped>
.nx-resize-handles {
  --handle: calc(var(--nxt-size-unit) * 0.55);
  --handle-offset: calc(var(--handle) / -2);

  position: absolute;
  inset: 0;
  z-index: var(--nxt-z-raised);
  pointer-events: none;
}
.nx-resize-handles__handle {
  position: absolute;
  width: var(--handle);
  height: var(--handle);
  border: calc(var(--nxt-hairline) * 1.5) solid var(--nxt-brand-bg);
  border-radius: calc(var(--nxt-hairline) * 2);
  background: var(--nxt-surface-raised-bg);
  box-shadow: var(--nxt-shadow-sm);
  opacity: 0;
  pointer-events: none;
  touch-action: none;
  transition:
    opacity var(--nxt-motion-fast) var(--nxt-ease-out),
    transform var(--nxt-motion-fast) var(--nxt-ease-out),
    background-color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-resize-handles.is-visible .nx-resize-handles__handle,
.nx-resize-handles__handle:focus-visible {
  opacity: 1;
  pointer-events: auto;
}
.nx-resize-handles__handle:hover,
.nx-resize-handles__handle.is-active {
  background: var(--nxt-brand-bg);
}
.nx-resize-handles__handle:focus-visible {
  outline: none;
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
/* Edge handles are pills along their edge, corners squares. */
.dir-n,
.dir-s {
  width: calc(var(--handle) * 2.2);
  left: 50%;
  margin-left: calc(var(--handle) * -1.1);
  cursor: ns-resize;
}
.dir-e,
.dir-w {
  height: calc(var(--handle) * 2.2);
  top: 50%;
  margin-top: calc(var(--handle) * -1.1);
  cursor: ew-resize;
}
.dir-n {
  top: var(--handle-offset);
}
.dir-s {
  bottom: var(--handle-offset);
}
.dir-e {
  right: var(--handle-offset);
}
.dir-w {
  left: var(--handle-offset);
}
.dir-nw {
  top: var(--handle-offset);
  left: var(--handle-offset);
  cursor: nwse-resize;
}
.dir-ne {
  top: var(--handle-offset);
  right: var(--handle-offset);
  cursor: nesw-resize;
}
.dir-se {
  right: var(--handle-offset);
  bottom: var(--handle-offset);
  cursor: nwse-resize;
}
.dir-sw {
  bottom: var(--handle-offset);
  left: var(--handle-offset);
  cursor: nesw-resize;
}
@media (prefers-reduced-motion: reduce) {
  .nx-resize-handles__handle {
    transition: none;
  }
}
@media (forced-colors: active) {
  .nx-resize-handles__handle {
    border-color: Highlight;
  }
}
</style>
